import { ReportDefinition } from '../models/report-definition';
import { ReportRowVm } from '../models/report-row-vm';
import {
   getFinancialPostingCashierId,
   getFinancialPostingDate,
   getFinancialPostingGuestName,
   getFinancialPostingReason,
   getFinancialPostingReceipt,
   getFinancialPostingRoom,
   getFinancialPostingSource,
   getFinancialPostingTime,
   getFinancialPostingTransactionCode,
   getFinancialPostingUserDisplay
} from '../utils/financial-posting.utils';
import { getFirstNumber, getFirstString } from '../utils/report-field.utils';
import {
   formatCurrency,
   formatFlexibleDate,
   formatTime
} from '../utils/report-formatting.utils';
import { getRealTyallsRowId } from '../utils/report-row-id.utils';

type PaidOutRow = {
   rowId: string;
   date: string;
   dateRaw: string;
   time: string;
   room: string;
   guestName: string;
   transaction: string;
   transactionCode: string;
   transactionDescription: string;
   reason: string;
   receipt: string;
   amount: string;
   amountRaw: number;
   user: string;
   cashierId: string;
   hasRealTyallsRowId: boolean;
};

function getRowId(row: unknown): string {
   const realTyallsRowId = getRealTyallsRowId(row);

   if (realTyallsRowId) {
      return realTyallsRowId;
   }

   return (
      getFirstString(row, [
         'ReceiptNumber',
         'receiptNumber',
         'ReceiptNo',
         'receiptNo',
         'TransactionNo',
         'transactionNo',
         'Id',
         'id'
      ]) ||
      `paid-out:${getFirstString(row, ['Date', 'date'])}:${getFirstString(row, [
         'RoomNumber',
         'roomNumber'
      ])}:${getFirstString(row, ['TransactionCode', 'transactionCode'])}`
   );
}

function amountFromMappedPostingDollars(row: unknown): number | null {
   const debit = getFirstNumber(row, [
      'Debit',
      'debit',
      'DebitAmount',
      'debitAmount',
      'DebitAmount.Amount',
      'debitAmount.amount'
   ]);

   if (debit !== null && debit > 0) {
      return debit;
   }

   const amount = getFirstNumber(row, [
      'Amount',
      'amount',
      'AmountRaw',
      'amountRaw',
      'PaidOutAmount',
      'paidOutAmount'
   ]);

   if (amount !== null && amount > 0) {
      return amount;
   }

   const credit = getFirstNumber(row, [
      'Credit',
      'credit',
      'CreditAmount',
      'creditAmount',
      'CreditAmount.Amount',
      'creditAmount.amount'
   ]);

   if (credit !== null && credit < 0) {
      return Math.abs(credit);
   }

   return null;
}

function amountFromOraclePostingDollars(row: unknown): number | null {
   const debitAmount = getFirstNumber(row, [
      'debitAmount.amount',
      'DebitAmount.Amount',
      'DebitAmount',
      'debitAmount'
   ]);

   if (debitAmount !== null && debitAmount > 0) {
      return debitAmount;
   }

   const amount = getFirstNumber(row, [
      'amount.amount',
      'postedAmount.amount',
      'Amount',
      'amount'
   ]);

   if (amount !== null && amount > 0) {
      return amount;
   }

   const creditAmount = getFirstNumber(row, [
      'creditAmount.amount',
      'CreditAmount.Amount',
      'CreditAmount',
      'creditAmount'
   ]);

   if (creditAmount !== null && creditAmount < 0) {
      return Math.abs(creditAmount);
   }

   return null;
}

function getTransactionDescription(row: unknown, isOracle: boolean): string {
   const description = getFirstString(row, [
      'TransactionCodeDescription',
      'transactionCodeDescription',
      'TransactionDescription',
      'transactionDescription',
      'CodeDescription',
      'codeDescription'
   ]);

   if (description) {
      return description;
   }

   if (isOracle) {
      return getFirstString(row, ['Description', 'description']);
   }

   return '';
}

function getTransactionDisplay(row: unknown, isOracle: boolean): string {
   const code = getFinancialPostingTransactionCode(row);
   const description = getTransactionDescription(row, isOracle);

   if (code && description) {
      return `${description} (${code})`;
   }

   return description || code;
}

function getReason(row: unknown): string {
   return getFinancialPostingReason(row, [
      [
         'Reason',
         'reason',
         'Description',
         'description',
         'Remark',
         'remark',
         'Reference',
         'reference',
         'TransactionRemark',
         'transactionRemark'
      ]
   ]);
}

function normalisePaidOutRows(
   sourceRows: unknown[],
   isOracle: boolean
): PaidOutRow[] {
   const rows: PaidOutRow[] = [];

   for (const sourceRow of sourceRows) {
      const amountRaw = isOracle
         ? amountFromOraclePostingDollars(sourceRow)
         : amountFromMappedPostingDollars(sourceRow);

      if (amountRaw === null || amountRaw <= 0) {
         continue;
      }

      const dateRaw = getFinancialPostingDate(sourceRow);
      const transactionCode = getFinancialPostingTransactionCode(sourceRow);

      rows.push({
         rowId: getRowId(sourceRow),
         date: formatFlexibleDate(dateRaw),
         dateRaw,
         time: formatTime(getFinancialPostingTime(sourceRow)),
         room: getFinancialPostingRoom(sourceRow),
         guestName: getFinancialPostingGuestName(sourceRow),
         transaction: getTransactionDisplay(sourceRow, isOracle),
         transactionCode,
         transactionDescription: getTransactionDescription(sourceRow, isOracle),
         reason: getReason(sourceRow),
         receipt: getFinancialPostingReceipt(sourceRow),
         amount: formatCurrency(amountRaw),
         amountRaw,
         user: getFinancialPostingUserDisplay(sourceRow),
         cashierId: getFinancialPostingCashierId(sourceRow),
         hasRealTyallsRowId: !!getRealTyallsRowId(sourceRow)
      });
   }

   return rows;
}

function sortPaidOutRows(rows: PaidOutRow[]): PaidOutRow[] {
   return [...rows].sort((a, b) => {
      const dateCompare = a.dateRaw.localeCompare(b.dateRaw);

      if (dateCompare !== 0) {
         return dateCompare;
      }

      return a.time.localeCompare(b.time);
   });
}

export const PAID_OUTS_DEFINITION: ReportDefinition = {
   reportType: 'PaidOuts',
   version: 3,
   title: 'Paid Outs',

   sorting: {
      enabled: true,
      persist: true
   },

   header: {
      title: 'Paid Outs Report'
   },

   rowChecks: {
      enabled: false,
      includeNestedRows: false,
      selectableRowTypes: ['paidOut']
   },

   annotations: {
      enabled: true,
      selectableRowTypes: ['paidOut'],
      disabledMessage:
         'Row comments and row highlighting are not available for this report layout. Please use the main report comments panel to add comments for this report.'
   },

   columns: [
      {
         key: 'date',
         header: 'TRN DATE',
         width: '105px',
         sort: {
            type: 'date',
            getValue: (row) => row.cells['dateRaw']
         }
      },
      {
         key: 'time',
         header: 'TIME',
         width: '75px',
         sort: { type: 'string' }
      },
      {
         key: 'room',
         header: 'ROOM',
         width: '80px',
         sort: { type: 'string' }
      },
      {
         key: 'guestName',
         header: 'GUEST NAME',
         width: '160px',
         sort: { type: 'string' }
      },
      {
         key: 'transaction',
         header: 'TRANSACTION',
         width: '190px',
         sort: { type: 'string' }
      },
      {
         key: 'reason',
         header: 'REASON',
         width: '360px',
         maxChars: 130,
         sort: { type: 'string' }
      },
      {
         key: 'receipt',
         header: 'RECEIPT',
         width: '130px',
         sort: { type: 'string' }
      },
      {
         key: 'amount',
         header: 'AMOUNT',
         width: '120px',
         sort: {
            type: 'number',
            getValue: (row) => row.cells['amountRaw'],
            defaultDirection: 'desc'
         }
      },
      {
         key: 'user',
         header: 'USER',
         width: '140px',
         sort: { type: 'string' }
      }
   ],

   filter: {
      fields: [
         {
            key: 'date',
            label: 'Transaction Date',
            type: 'date',
            getValue: (row) => row.cells['dateRaw']
         },
         { key: 'time', label: 'Time', type: 'string' },
         { key: 'room', label: 'Room', type: 'string' },
         { key: 'guestName', label: 'Guest Name', type: 'string' },
         { key: 'transaction', label: 'Transaction', type: 'string' },
         { key: 'reason', label: 'Reason', type: 'string' },
         { key: 'receipt', label: 'Receipt', type: 'string' },
         {
            key: 'amount',
            label: 'Amount',
            type: 'number',
            getValue: (row) => row.cells['amountRaw']
         },
         { key: 'user', label: 'User', type: 'string' }
      ]
   },

   flattenRows: (reportJson: unknown): ReportRowVm[] => {
      const { rows: sourceRows, isOracle } = getFinancialPostingSource(reportJson, [
         'PaidOuts',
         'PaidOutsReport'
      ]);

      const paidOutRows = sortPaidOutRows(normalisePaidOutRows(sourceRows, isOracle));

      return paidOutRows.map((row) => ({
         rowId: row.rowId,
         isAnnotatable: row.hasRealTyallsRowId,
         cells: {
            rowType: 'paidOut',
            date: row.date,
            dateRaw: row.dateRaw,
            time: row.time,
            room: row.room,
            guestName: row.guestName,
            transaction: row.transaction,
            transactionCode: row.transactionCode,
            transactionDescription: row.transactionDescription,
            reason: row.reason,
            receipt: row.receipt,
            amount: row.amount,
            amountRaw: row.amountRaw,
            user: row.user,
            cashierId: row.cashierId
         }
      }));
   }
};
